import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CircularProgress, Dialog } from "@mui/material";
import { Link } from "react-router-dom";
import { TextInput } from "@dooglie/ui";
import {
  apiErrorMessage,
  DemoMeeting,
  DemoMeetings,
  DemoPrepareStatus,
  DemoScenario,
  demoSession,
  impersonate,
} from "../API";
import { Dispatch, RootState } from "../store";
import {
  allTicksOn,
  crmLinkRefusalMessage,
  isPreparing,
  PREPARE_STEPS,
  prospectTicks,
  timelineStep,
} from "../models/demo";
import "../styles/ui.css";
import "../styles/demo.css";

type PrepareDemoDialogProps = {
  open: boolean;
  onClose: () => void;
};

/** How often the dialog asks the server where the preparation has got to. */
export const POLL_MS = 5000;

/** How often a ready demo is asked how far the prospect has got, until all three ticks are on. */
export const READY_POLL_MS = 10000;

const STEP_LABELS: Record<(typeof PREPARE_STEPS)[number], string> = {
  crawling: "Reading the prospect's website",
  building_tenant: "Building the workspace and organisation",
  warming: "Finishing up",
};

const SCENARIOS: { scenario: DemoScenario; label: string }[] = [
  { scenario: "candidate", label: "I'll play a candidate" },
  { scenario: "client", label: "I'll play a client" },
];

const fmtWhen = (iso: string | null | undefined) =>
  iso
    ? new Date(iso).toLocaleString(undefined, {
        weekday: "short",
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

/** Step 1: the Dooglie sign-in link, built from this webapp's own origin. */
const DooglieLinkButton: React.FC<{ email: string; primary: boolean }> = ({ email, primary }) => {
  const [note, setNote] = useState<{ text: string; failed: boolean } | null>(null);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(
        `${window.location.origin}/login?email=${encodeURIComponent(email)}`,
      );
      setNote({ text: "Link copied", failed: false });
    } catch {
      setNote({ text: "Could not copy the link. Press the button to try again.", failed: true });
    }
  };
  return (
    <div className="dg-demo-link-action">
      <button
        type="button"
        className={`dg-btn ${primary ? "dg-btn-primary" : ""}`}
        onClick={() => void copy()}
      >
        Copy Dooglie sign-in link
      </button>
      {note && (
        <div
          className={note.failed ? "dg-banner dg-banner-error" : "dg-demo-hint"}
          role={note.failed ? "alert" : "status"}
        >
          {note.text}
        </div>
      )}
    </div>
  );
};

/**
 * Mints the demo's sign-in link and copies the bare url. The link is stable:
 * it works until the expiry the server returns.
 */
const CrmLinkButton: React.FC<{ primary: boolean }> = ({ primary }) => {
  const [working, setWorking] = useState(false);
  const [note, setNote] = useState<{ text: string; failed: boolean } | null>(null);
  const mint = async () => {
    setWorking(true);
    setNote(null);
    try {
      const link = await demoSession({ action: "crm_link" });
      try {
        await navigator.clipboard.writeText(link.url);
        setNote({
          text: link.expires_at
            ? `Link copied — works until ${fmtWhen(link.expires_at)}`
            : "Link copied",
          failed: false,
        });
      } catch {
        setNote({
          text: "Link minted but could not be copied. Press the button to try again.",
          failed: true,
        });
      }
    } catch (error) {
      setNote({ text: crmLinkRefusalMessage(error), failed: true });
    } finally {
      setWorking(false);
    }
  };
  return (
    <div className="dg-demo-link-action">
      <button
        type="button"
        className={`dg-btn ${primary ? "dg-btn-primary" : ""}`}
        disabled={working}
        onClick={() => void mint()}
      >
        Copy CRM sign-in link
      </button>
      {note && (
        <div
          className={note.failed ? "dg-banner dg-banner-error" : "dg-demo-hint"}
          role={note.failed ? "alert" : "status"}
        >
          {note.text}
        </div>
      )}
    </div>
  );
};

/** Signed in to Dooglie → Extension active → CRM link opened; tolerant of a missing `prospect_progress`. */
const ProgressChecklist: React.FC<{ status: DemoPrepareStatus }> = ({ status }) => {
  const ticks = prospectTicks(status);
  const when = (iso: string | null) => (iso ? ` · ${fmtWhen(iso)}` : "");
  return (
    <ul className="dg-demo-checklist" aria-label="Prospect progress">
      {ticks.map((t) => (
        <li key={t.key} className={`dg-demo-check ${t.done ? "done" : "todo"}`} data-done={t.done}>
          <span className="dg-demo-check-box" aria-hidden="true">
            {t.done ? "✓" : ""}
          </span>
          <span>
            <span>{t.label}</span>
            {t.note && (
              <span className="dg-demo-hint" title={t.note}>
                {" "}
                {t.note}
              </span>
            )}
            {t.done && when(t.at)}
          </span>
          <span className="dg-demo-sr">{t.done ? "done" : "not yet"}</span>
        </li>
      ))}
    </ul>
  );
};

// ── Step 1: pick the meeting, the prospect and the scenario ────────────────

const MeetingPicker: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const dispatch = useDispatch<Dispatch>();
  const busy = useSelector((s: RootState) => s.demo.busy);
  const error = useSelector((s: RootState) => s.demo.error);
  const errorCode = useSelector((s: RootState) => s.demo.errorCode);
  const [meetings, setMeetings] = useState<DemoMeetings | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [meetingId, setMeetingId] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [scenario, setScenario] = useState<DemoScenario>("candidate");
  const [site, setSite] = useState("");

  const load = () => {
    setLoadError(null);
    dispatch.demo
      .fetchMeetings()
      .then(setMeetings)
      .catch((e: unknown) => setLoadError(apiErrorMessage(e)));
  };
  useEffect(load, [dispatch]);

  const meeting: DemoMeeting | undefined = meetings?.meetings.find((m) => m.id === meetingId);

  const pickMeeting = (m: DemoMeeting) => {
    setMeetingId(m.id);
    setGuestEmail(m.guests.length === 1 ? m.guests[0].email : "");
    setSite("");
  };

  const canPrepare = meeting != null && guestEmail !== "" && !busy;

  const prepare = async () => {
    if (!meeting || !canPrepare) return;
    try {
      await dispatch.demo.prepare({
        meeting_id: meeting.id,
        prospect_email: guestEmail,
        scenario,
        site: site.trim() || undefined,
      });
    } catch {
      // The refusal is in state.demo.error, shown below.
    }
  };

  return (
    <>
      <div className="dg-demo-body">
        {meetings == null && !loadError && (
          <div className="dg-demo-loading">
            <CircularProgress color="primary" />
          </div>
        )}
        {loadError && (
          <div className="dg-banner dg-banner-row">
            <span>{loadError}</span>
            <button type="button" className="dg-btn dg-btn-primary dg-btn-sm" onClick={load}>
              Try again
            </button>
          </div>
        )}
        {meetings?.calendar === "none" && (
          <div className="dg-demo-empty">
            Connect your calendar to Dooglie first — demos are prepared from a meeting on it.
          </div>
        )}
        {meetings != null && meetings.calendar !== "none" && meetings.meetings.length === 0 && (
          <div className="dg-demo-empty">
            No meetings with someone outside Dooglie in the last day or the next 7 days.
          </div>
        )}
        {meetings != null && meetings.meetings.length > 0 && (
          <ul className="dg-demo-meetings" aria-label="Upcoming meetings">
            {meetings.meetings.map((m) => (
              <li key={m.id}>
                <button
                  type="button"
                  className={`dg-demo-meeting ${m.id === meetingId ? "selected" : ""}`}
                  aria-pressed={m.id === meetingId}
                  onClick={() => pickMeeting(m)}
                >
                  <span className="dg-demo-meeting-title">{m.summary || "Untitled meeting"}</span>
                  <span className="dg-demo-meeting-meta">
                    {fmtWhen(m.start_time)} · {m.guests.map((g) => g.name || g.email).join(", ")}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="dg-demo-footer">
        {meeting && meeting.guests.length > 1 && (
          <div className="dg-field">
            <label htmlFor="dg-demo-guest">Who is the prospect?</label>
            <select
              id="dg-demo-guest"
              value={guestEmail}
              disabled={busy}
              onChange={(e) => setGuestEmail(e.target.value)}
            >
              <option value="">Choose the guest you're demoing to…</option>
              {meeting.guests.map((g) => (
                <option key={g.email} value={g.email}>
                  {g.name ? `${g.name} — ${g.email}` : g.email}
                </option>
              ))}
            </select>
          </div>
        )}
        {meeting && meeting.guests.length === 1 && (
          <div className="dg-demo-footer-text">
            Preparing for <b>{meeting.guests[0].name || meeting.guests[0].email}</b> (
            {meeting.guests[0].email})
          </div>
        )}
        <div className="dg-field">
          <label htmlFor="dg-demo-site">Company website (optional)</label>
          <TextInput
            id="dg-demo-site"
            field="demo.company_site"
            hint="A company website address"
            value={site}
            disabled={busy}
            onValueChange={setSite}
          />
        </div>
        <div className="dg-demo-tabs" role="tablist" aria-label="Who you play in the demo">
          {SCENARIOS.map((s) => (
            <button
              key={s.scenario}
              type="button"
              role="tab"
              aria-selected={scenario === s.scenario}
              className="dg-demo-tab"
              onClick={() => setScenario(s.scenario)}
            >
              {s.label}
            </button>
          ))}
        </div>
        <div className="dg-demo-actions">
          <button type="button" className="dg-btn dg-btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="dg-btn dg-btn-primary"
            disabled={!canPrepare}
            onClick={() => void prepare()}
          >
            {busy ? "Starting…" : "Prepare demo"}
          </button>
        </div>
        {error && errorCode === "salesperson_has_no_name" ? (
          <div className="dg-banner dg-demo-error">
            Set your first and last name in{" "}
            <Link to="/settings" onClick={onClose}>
              Settings
            </Link>{" "}
            before preparing a demo — the prospect's CRM will show you under that name.
          </div>
        ) : (
          error && <div className="dg-banner dg-demo-error">{error}</div>
        )}
      </div>
    </>
  );
};

// ── Step 2: where the preparation has got to ───────────────────────────────

const Timeline: React.FC<{ status: DemoPrepareStatus }> = ({ status }) => {
  const shown = timelineStep(status.status);
  const at = shown == null ? -1 : PREPARE_STEPS.indexOf(shown);
  return (
    <ol className="dg-demo-timeline" aria-label="Preparation steps">
      {PREPARE_STEPS.map((step, i) => {
        const state = at < 0 ? "todo" : i < at ? "done" : i === at ? "now" : "todo";
        return (
          <li
            key={step}
            className={`dg-demo-step ${state}`}
            aria-current={state === "now" ? "step" : undefined}
          >
            <span className="dg-demo-step-dot" />
            {STEP_LABELS[step]}
          </li>
        );
      })}
    </ol>
  );
};

// ── Step 3: ready ───────────────────────────────────────────────────────────

const ReadyPanel: React.FC<{ status: DemoPrepareStatus }> = ({ status }) => {
  const hero = status.hero;
  const heroLinks: [string, string | null | undefined][] = hero
    ? [
        ["Candidate", hero.candidate_url],
        ["Contact", hero.contact_url],
        ["Job", hero.job_url],
        ["Company", hero.company_url],
      ]
    : [];
  const shownHero = heroLinks.filter(([, url]) => url);
  const warnings = status.warnings ?? [];
  const [signedIn, extensionActive] = prospectTicks(status);
  const readyForCrm = signedIn.done && extensionActive.done;
  const hasDetails = shownHero.length > 0 || warnings.length > 0 || !!status.expires_at;
  return (
    <div className="dg-demo-ready-grid">
      {status.prospect_domain && (
        <div className="dg-demo-hint">
          Built from {status.prospect_domain}
          {status.brief?.firm_name ? ` (${status.brief.firm_name})` : ""}
        </div>
      )}
      {status.brief?.confidence === "low" && (
        <div className="dg-banner">
          That site gave us little to go on, so this world is a rough guess. Check it before the
          call.
        </div>
      )}
      <div className="dg-demo-step">
        <b>1. Dooglie</b>
        <DooglieLinkButton email={status.prospect_email ?? ""} primary={!readyForCrm} />
        <div className="dg-demo-hint">
          Send this first. They sign in, then Dooglie offers the extension.
        </div>
      </div>
      <div className="dg-demo-step">
        <b>2. CRM</b>
        <CrmLinkButton primary={readyForCrm} />
        <div className="dg-demo-hint">
          Send this once they're signed in to Dooglie with the extension — otherwise they'll need to
          refresh the CRM page.
        </div>
      </div>
      <ProgressChecklist status={status} />
      {hasDetails && (
        <details className="dg-demo-details">
          <summary>Details</summary>
          <div className="dg-demo-details-body">
            {shownHero.length > 0 && (
              <div className="dg-demo-kv">
                <span>Hero records</span>
                <span className="dg-demo-links">
                  {shownHero.map(([label, url]) => (
                    <a key={label} href={url!} target="_blank" rel="noopener noreferrer">
                      {label} ↗
                    </a>
                  ))}
                </span>
              </div>
            )}
            {warnings.length > 0 && (
              <div className="dg-demo-warnings">
                <b>Builder warnings ({warnings.length})</b>
                <ul>
                  {warnings.map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                </ul>
              </div>
            )}
            {status.expires_at && (
              <div className="dg-demo-hint">
                Torn down automatically after {fmtWhen(status.expires_at)}.
              </div>
            )}
          </div>
        </details>
      )}
    </div>
  );
};

/**
 * Prepare a demo (Dooglie staff): pick an upcoming meeting with a prospect,
 * and the server builds them their own Dooglie org and Cowtrumpet world from
 * their website. The dialog follows `prepare_status` every five seconds
 * until it is ready (or failed), then hands over one sign-in link button, a
 * live checklist of how far the prospect has got (polled every ten seconds
 * until all three ticks are on), a way to look at it as the prospect, and a
 * way to tear it down.
 */
const PrepareDemoDialog: React.FC<PrepareDemoDialogProps> = ({ open, onClose }) => {
  const dispatch = useDispatch<Dispatch>();
  const status = useSelector((s: RootState) => s.demo.status);
  const busy = useSelector((s: RootState) => s.demo.busy);
  const error = useSelector((s: RootState) => s.demo.error);
  const [loaded, setLoaded] = useState(false);
  const [tearingDown, setTearingDown] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const timer = useRef<number | undefined>(undefined);

  // Every opening asks for the current status before showing anything.
  useEffect(() => {
    if (!open) return;
    let live = true;
    setLoaded(false);
    setActionError(null);
    dispatch.demo.clearError();
    void dispatch.demo.fetchStatus().then(() => {
      if (live) setLoaded(true);
    });
    return () => {
      live = false;
    };
  }, [open, dispatch]);

  const step = status?.status ?? "none";
  const preparingPoll = open && loaded && (isPreparing(status) || (tearingDown && step !== "none"));
  const readyPoll = open && loaded && !preparingPoll && step === "ready" && !allTicksOn(status);
  const polling = preparingPoll || readyPoll;
  const pollMs = preparingPoll ? POLL_MS : READY_POLL_MS;

  // Poll while the server is working, one request at a time.
  useEffect(() => {
    if (!polling) return;
    let live = true;
    const tick = () => {
      timer.current = window.setTimeout(async () => {
        await dispatch.demo.fetchStatus();
        if (live) tick();
      }, pollMs);
    };
    tick();
    return () => {
      live = false;
      window.clearTimeout(timer.current);
    };
  }, [polling, pollMs, dispatch]);

  useEffect(() => {
    if (step === "none") setTearingDown(false);
  }, [step]);

  const openAsProspect = async () => {
    if (!status?.prospect_user_id) return;
    setActionError(null);
    try {
      await impersonate({ user_id: status.prospect_user_id });
      window.location.reload();
    } catch (e) {
      setActionError(apiErrorMessage(e));
    }
  };

  const retry = async () => {
    try {
      await dispatch.demo.retry();
    } catch {
      // Shown from state.demo.error.
    }
  };

  const tearDown = async () => {
    if (
      !window.confirm("Tear this demo down? The prospect's org, logins and CRM world are deleted.")
    ) {
      return;
    }
    try {
      await dispatch.demo.teardown();
      setTearingDown(true);
    } catch {
      // Shown from state.demo.error.
    }
  };

  // A failed teardown cannot be retried as a build: only another teardown
  // finishes it (the server refuses `retry` with `teardown_failed`).
  const teardownFailed = step === "failed" && (status?.error ?? "").startsWith("teardown:");
  const picking = loaded && step === "none" && !tearingDown;
  const title = picking
    ? "Prepare a demo"
    : tearingDown
      ? "Tearing the demo down"
      : step === "ready"
        ? `${status?.org_name ?? "The demo"} is ready`
        : step === "failed"
          ? "Preparing the demo failed"
          : `Preparing a demo for ${status?.prospect_email ?? "the prospect"}`;

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth={false}
      aria-labelledby="dg-demo-title"
      slotProps={{
        paper: {
          className: "dg-demo-panel",
          sx: {
            width: "min(640px, 94vw)",
            maxHeight: "88vh",
            m: 2,
            borderRadius: "22px",
            boxShadow: "var(--shadow-panel)",
          },
        },
        backdrop: { className: "dg-demo-backdrop" },
      }}
    >
      <div className="dg-demo-head">
        <button
          type="button"
          className="dg-demo-close"
          aria-label="Close"
          onClick={onClose}
          disabled={busy}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
        <div className="dg-eyebrow dg-demo-eyebrow">Demo</div>
        <h2 id="dg-demo-title" className="dg-demo-title">
          {title}
        </h2>
        {picking && (
          <p className="dg-demo-sub">
            Pick the meeting. Dooglie reads the prospect's website and builds them their own org and
            CRM, with you in it, in about four minutes.
          </p>
        )}
      </div>

      {!loaded && (
        <div className="dg-demo-body">
          <div className="dg-demo-loading">
            <CircularProgress color="primary" />
          </div>
        </div>
      )}

      {picking && <MeetingPicker onClose={onClose} />}

      {loaded && !picking && status && (
        <>
          <div className="dg-demo-body">
            {tearingDown ? (
              <div className="dg-demo-loading">
                <CircularProgress color="primary" />
              </div>
            ) : step === "failed" ? (
              <div className="dg-banner">{status.error || "Something went wrong."}</div>
            ) : step === "ready" ? (
              <ReadyPanel status={status} />
            ) : (
              <>
                <Timeline status={status} />
              </>
            )}
          </div>
          <div className="dg-demo-footer">
            <div className="dg-demo-actions">
              {step === "ready" && !tearingDown && status.prospect_user_id && (
                <button
                  type="button"
                  className="dg-btn dg-btn-primary"
                  disabled={busy}
                  onClick={() => void openAsProspect()}
                >
                  Open as the prospect
                </button>
              )}
              {step === "failed" && !tearingDown && !teardownFailed && (
                <button
                  type="button"
                  className="dg-btn dg-btn-primary"
                  disabled={busy}
                  onClick={() => void retry()}
                >
                  Retry
                </button>
              )}
              {!tearingDown && (
                <button
                  type="button"
                  className="dg-btn dg-btn-danger"
                  disabled={busy}
                  onClick={() => void tearDown()}
                >
                  {teardownFailed ? "Tear down again" : "Tear down"}
                </button>
              )}
              <button
                type="button"
                className="dg-btn dg-btn-ghost"
                onClick={onClose}
                disabled={busy}
              >
                Close
              </button>
            </div>
            {(actionError || error) && (
              <div className="dg-banner dg-demo-error">{actionError || error}</div>
            )}
          </div>
        </>
      )}
    </Dialog>
  );
};

export default PrepareDemoDialog;
